import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { Monitor, Target, Users, Calendar, MessageCircle, Instagram, Facebook, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import MobileTraining from '../components/MobileTraining';
import TrainingMethodModal from '../components/TrainingMethodModal';
import ContactModal from '../components/ContactModal';
import GalleryModal from '../components/GalleryModal';
import Reviews from '../components/Reviews';
import SEOHead from '../components/SEOHead';
import AnnouncementBanner from '../components/AnnouncementBanner';
import HeroBackgroundVideo from '../components/HeroBackgroundVideo';
import SafeImage from '../components/SafeImage';
import { resolveInstagramUrl } from '../constants/social';
import {
  CALENDLY_DEFAULT,
  CALENDLY_GROUP_CLASS,
  CALENDLY_MOBILE,
  CALENDLY_ONLINE,
} from '../constants/calendly';
import { useCalendlyEmbed } from '../context/CalendlyEmbedContext';
import '../App.css';

function Home() {
  const { openCalendly } = useCalendlyEmbed();
  const [hero, setHero] = useState({ title: '', subtitle: '', buttonText: '' });
  const [growthWays, setGrowthWays] = useState([]);
  const [services, setServices] = useState([]);
  const [about, setAbout] = useState({ title: 'Meet Carlo', content: '', certifications: [], imageUrl: '' });
  const [gallery, setGallery] = useState([]);
  const [youtubeVideo, setYoutubeVideo] = useState({ url: '', title: '' });
  const [pricing, setPricing] = useState([]);
  const [contact, setContact] = useState({ title: 'Ready to Start?', subtitle: '' });
  const [footer, setFooter] = useState({ copyright: '© 2026 Bodyworks Training LLC. All rights reserved.' });
  const [announcement, setAnnouncement] = useState({ active: false, message: '' });
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [instagramUrl, setInstagramUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const galleryScrollRef = useRef(null);
  const [trainers, setTrainers] = useState([]);
  const [mobileTraining, setMobileTraining] = useState({});
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [
        heroDoc,
        growthSnapshot,
        servicesSnapshot,
        aboutDoc,
        pricingSnapshot,
        contactDoc,
        footerDoc,
        gallerySnapshot,
        videoDoc,
        announcementDoc,
        trainersSnapshot,
        mobileTrainingDoc,
        reviewsSnapshot,
      ] = await Promise.all([
        getDoc(doc(db, 'settings', 'hero')),
        getDocs(collection(db, 'growthWays')),
        getDocs(collection(db, 'services')),
        getDoc(doc(db, 'settings', 'about')),
        getDocs(collection(db, 'pricing')),
        getDoc(doc(db, 'settings', 'contact')),
        getDoc(doc(db, 'settings', 'footer')),
        getDocs(collection(db, 'gallery')),
        getDoc(doc(db, 'settings', 'youtubeVideo')),
        getDoc(doc(db, 'settings', 'announcement')),
        getDocs(collection(db, 'trainers')),
        getDoc(doc(db, 'settings', 'mobileTraining')),
        getDocs(collection(db, 'reviews')),
      ]);

      if (heroDoc.exists()) setHero(heroDoc.data());

      setGrowthWays(
        growthSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.order - b.order)
      );

      setServices(
        servicesSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.order - b.order)
      );

      if (aboutDoc.exists()) setAbout(aboutDoc.data());

      setPricing(
        pricingSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.order - b.order)
      );

      if (contactDoc.exists()) setContact(contactDoc.data());

      if (footerDoc.exists()) {
        setFooter(footerDoc.data());
        setInstagramUrl(resolveInstagramUrl(footerDoc.data().instagramUrl));
        if (footerDoc.data().tiktokUrl) setTiktokUrl(footerDoc.data().tiktokUrl);
        if (footerDoc.data().facebookUrl) setFacebookUrl(footerDoc.data().facebookUrl);
      } else {
        setInstagramUrl(resolveInstagramUrl());
      }

      setGallery(
        gallerySnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.order - b.order)
      );

      if (videoDoc.exists()) setYoutubeVideo(videoDoc.data());
      if (announcementDoc.exists()) setAnnouncement(announcementDoc.data());

      setTrainers(trainersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

      if (mobileTrainingDoc.exists()) setMobileTraining(mobileTrainingDoc.data());

      setReviews(
        reviewsSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((review) => review.active !== false)
          .sort((a, b) => (b.order || 0) - (a.order || 0))
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    // Smooth scrolling for anchor links
    const handleClick = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (target) {
        e.preventDefault();
        const targetElement = document.querySelector(target.getAttribute('href'));
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };

    document.addEventListener('click', handleClick);

    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.service-card, .stat-item, .pricing-card, .growth-card').forEach(el => {
      observer.observe(el);
    });

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    const openContact = () => setShowContactModal(true);
    const openTraining = () => setShowTrainingModal(true);
    window.addEventListener('bodyworks-open-contact', openContact);
    window.addEventListener('bodyworks-open-training', openTraining);
    return () => {
      window.removeEventListener('bodyworks-open-contact', openContact);
      window.removeEventListener('bodyworks-open-training', openTraining);
    };
  }, []);


  return (
    <>
      <SEOHead />
      <AnnouncementBanner
        announcement={announcement}
        visible={showAnnouncement}
        onDismiss={() => setShowAnnouncement(false)}
      />

      {/* Hero Section */}
      <section className="hero">
        <HeroBackgroundVideo videoUrl={hero.backgroundVideoUrl} />
        <div className="hero-bg"></div>
        <div className="hero-grid"></div>
        <div className="hero-content">
          <h1 className="hero-title">{hero.title}</h1>
          <p className="hero-subtitle">{hero.subtitle}</p>
          <button onClick={() => {
            const element = document.getElementById('growth-section');
            if (element) {
              const offset = 80;
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - offset;
              window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            }
          }} className="cta-button">
            {hero.buttonText}
          </button>
        </div>
        <div className="scroll-indicator">Scroll Down ↓</div>
      </section>

      {/* Training Method Modal */}
      <TrainingMethodModal
        isOpen={showTrainingModal}
        onClose={() => setShowTrainingModal(false)}
        methods={growthWays}
        onSelectMethod={(method) => {
          console.log('Selected method:', method);
        }}
        onBook={(bookingUrl) => {
          setShowTrainingModal(false);
          openCalendly(bookingUrl);
        }}
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {/* Gallery Modal */}
      <GalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        images={gallery}
        currentIndex={currentGalleryIndex}
        onNavigate={setCurrentGalleryIndex}
      />

      {/* YouTube Video Section */}
      {youtubeVideo.url && (
        <section className="video-section">
          <div className="video-container">
            <h2 className="section-title">{youtubeVideo.title || 'Watch Our Story'}</h2>
            <div className="video-wrapper">
              <iframe
                className="youtube-iframe"
                src={`https://www.youtube.com/embed/${youtubeVideo.url.includes('watch?v=') ? youtubeVideo.url.split('watch?v=')[1].split('&')[0] : youtubeVideo.url.split('/').pop()}`}
                title={youtubeVideo.title || 'YouTube video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* 3 Ways to Grow Section */}
      <section className="growth-section" id="growth-section">
        <h2 className="section-title">3 Ways to Grow</h2>
        <p className="section-subtitle" style={{textAlign: 'center', marginBottom: '3rem', fontSize: '1.1rem', color: 'rgba(245, 245, 245, 0.7)'}}>
          Choose the training method that fits your lifestyle and goals
        </p>
        <div className="growth-grid">
          {growthWays.length > 0 ? (
            growthWays.map((item, index) => {
              const icons = [Monitor, Target, Users];
              const Icon = icons[index % icons.length] || Monitor;
              return (
                <div key={item.id} className={`growth-card ${item.featured ? 'featured-growth' : ''}`}>
                  {item.featured && <div className="growth-badge">Most Popular</div>}
                  <div className="growth-icon">
                    {item.imageUrl ? (
                      <SafeImage
                        src={item.imageUrl}
                        alt={item.title ? `${item.title}` : ''}
                        className="growth-card-image"
                      />
                    ) : (
                      <Icon size={64} strokeWidth={1.5} aria-hidden />
                    )}
                  </div>
                  <h3 className="growth-title">{item.title}</h3>
                  <p className="growth-description">{item.description}</p>
                  <div className="growth-features">
                    {item.features && item.features.map((feature, idx) => (
                      <span key={idx} className="growth-feature">{feature}</span>
                    ))}
                  </div>
                  <div style={{display: 'flex', gap: '0.75rem', marginTop: '2rem'}}>
                    <button
                      type="button"
                      className="growth-button primary"
                      onClick={() => openCalendly(item.calendlyUrl || CALENDLY_DEFAULT)}
                    >
                      Sign Up
                    </button>
                    {item.learnMoreUrl ? (
                      <Link
                        to={item.learnMoreUrl}
                        className="growth-button secondary"
                        style={{textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                      >
                        Learn More
                      </Link>
                    ) : (
                      <button
                        onClick={() => setShowContactModal(true)}
                        className="growth-button secondary"
                      >
                        Learn More
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <>
              <div className="growth-card">
                <div className="growth-icon"><Monitor size={64} strokeWidth={1.5} /></div>
                <h3 className="growth-title">Online Training</h3>
                <p className="growth-description">Train anywhere, anytime with custom programs delivered through our app.</p>
                <div className="growth-features">
                  <span className="growth-feature">Custom Programs</span>
                  <span className="growth-feature">Video Demos</span>
                  <span className="growth-feature">Progress Tracking</span>
                </div>
                <div style={{display: 'flex', gap: '0.75rem', marginTop: '2rem'}}>
                  <button type="button" className="growth-button primary" onClick={() => openCalendly(CALENDLY_ONLINE)}>Sign Up</button>
                  <button onClick={() => setShowContactModal(true)} className="growth-button secondary">Contact Us</button>
                </div>
              </div>
              <div className="growth-card featured-growth">
                <div className="growth-badge">Most Popular</div>
                <div className="growth-icon"><Target size={64} strokeWidth={1.5} /></div>
                <h3 className="growth-title">1-on-1 Training</h3>
                <p className="growth-description">Get personalized attention with dedicated one-on-one sessions.</p>
                <div className="growth-features">
                  <span className="growth-feature">Personal Attention</span>
                  <span className="growth-feature">Custom Plans</span>
                  <span className="growth-feature">Guaranteed Results</span>
                </div>
                <div style={{display: 'flex', gap: '0.75rem', marginTop: '2rem'}}>
                  <button type="button" className="growth-button primary" onClick={() => openCalendly(CALENDLY_MOBILE)}>Sign Up</button>
                  <Link to="/mobile" className="growth-button secondary" style={{textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Learn More</Link>
                </div>
              </div>
              <div className="growth-card">
                <div className="growth-icon"><Users size={64} strokeWidth={1.5} /></div>
                <h3 className="growth-title">Group Training</h3>
                <p className="growth-description">Build community while building strength.</p>
                <div className="growth-features">
                  <span className="growth-feature">Team Energy</span>
                  <span className="growth-feature">Cost Effective</span>
                  <span className="growth-feature">Motivated Community</span>
                </div>
                <div style={{display: 'flex', gap: '0.75rem', marginTop: '2rem'}}>
                  <button type="button" className="growth-button primary" onClick={() => openCalendly(CALENDLY_GROUP_CLASS)}>Sign Up</button>
                  <Link to="/group" className="growth-button secondary" style={{textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Learn More</Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section className="services" id="services">
        <h2 className="section-title">What We Help You Achieve</h2>
        <div className="services-grid">
          {services.length > 0 ? (
            services.map((service, index) => (
              <div key={service.id} className="service-card">
                <div className="service-number">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            ))
          ) : (
            <>
              <div className="service-card">
                <div className="service-number">01</div>
                <h3 className="service-title">Weight Loss</h3>
                <p className="service-description">
                  Sustainable fat loss programs that help you shed pounds and keep them off. Combining smart training, nutrition guidance, and lifestyle coaching for lasting results.
                </p>
              </div>
              <div className="service-card">
                <div className="service-number">02</div>
                <h3 className="service-title">Gain Muscle</h3>
                <p className="service-description">
                  Build lean, functional muscle with science-based strength training. Progressive programs designed to maximize growth while preventing injury.
                </p>
              </div>
              <div className="service-card">
                <div className="service-number">03</div>
                <h3 className="service-title">Train for Sporting Events</h3>
                <p className="service-description">
                  Sport-specific training programs to improve your performance. Whether it's a marathon, competition, or personal challenge, we'll get you ready.
                </p>
              </div>
              <div className="service-card">
                <div className="service-number">04</div>
                <h3 className="service-title">Better Clarity & Wellness</h3>
                <p className="service-description">
                  Improve mental clarity, energy levels, and overall wellness through movement. Exercise programs designed to enhance your quality of life and daily performance.
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Training Plans Section */}
      <section className="pricing" id="pricing">
        <h2 className="section-title">Training Plans</h2>
        <p className="section-subtitle" style={{textAlign: 'center', marginBottom: '3rem', fontSize: '1.1rem', color: 'rgba(245, 245, 245, 0.7)', maxWidth: '800px', margin: '0 auto 3rem'}}>
          Choose the plan that fits your goals. Contact us for pricing details.
        </p>
        <div className="pricing-grid">
          {pricing.length > 0 ? (
            pricing.map((plan) => (
              <div key={plan.id} className={`pricing-card ${plan.featured ? 'featured' : ''}`}>
                {plan.featured && <div className="featured-badge">Most Popular</div>}
                <h3 className="pricing-title">{plan.title}</h3>
                <ul className="pricing-features">
                  {plan.features && plan.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: 'auto'}}>
                  {plan.calendlyUrl ? (
                    <button
                      type="button"
                      className="cta-button"
                      style={{textAlign: 'center', width: '100%' }}
                      onClick={() => openCalendly(plan.calendlyUrl)}
                    >
                      Get Started
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowTrainingModal(true)}
                      className="cta-button"
                      style={{ textAlign: 'center', width: '100%' }}
                    >
                      Get Started
                    </button>
                  )}
                  {plan.learnMoreUrl && (
                    plan.learnMoreUrl.startsWith('/') ? (
                      <Link
                        to={plan.learnMoreUrl}
                        className="cta-button secondary"
                        style={{textDecoration: 'none', textAlign: 'center', background: 'transparent', border: '2px solid rgba(255, 0, 0, 0.5)', display: 'block'}}
                      >
                        Learn More
                      </Link>
                    ) : (
                      <a
                        href={plan.learnMoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cta-button secondary"
                        style={{textDecoration: 'none', textAlign: 'center', background: 'transparent', border: '2px solid rgba(255, 0, 0, 0.5)'}}
                      >
                        Learn More
                      </a>
                    )
                  )}
                </div>
              </div>
            ))
          ) : (
            <>
              <div className="pricing-card">
                <h3 className="pricing-title">Starter</h3>
                <ul className="pricing-features">
                  <li>1 Session per Week</li>
                  <li>Customized Workout Plan</li>
                  <li>Progress Tracking</li>
                  <li>Email Support</li>
                </ul>
                <button onClick={() => setShowContactModal(true)} className="cta-button">Get Started</button>
              </div>
              <div className="pricing-card featured">
                <div className="featured-badge">Most Popular</div>
                <h3 className="pricing-title">Transform</h3>
                <ul className="pricing-features">
                  <li>2 Sessions per Week</li>
                  <li>Custom Training & Nutrition</li>
                  <li>Weekly Check-ins</li>
                  <li>24/7 Text Support</li>
                  <li>Body Composition Analysis</li>
                </ul>
                <button onClick={() => setShowContactModal(true)} className="cta-button">Get Started</button>
              </div>
              <div className="pricing-card">
                <h3 className="pricing-title">Elite</h3>
                <ul className="pricing-features">
                  <li>3 Sessions per Week</li>
                  <li>Full Nutrition Coaching</li>
                  <li>Daily Check-ins</li>
                  <li>Priority Support</li>
                  <li>Advanced Analytics</li>
                  <li>Supplement Guidance</li>
                </ul>
                <button onClick={() => setShowContactModal(true)} className="cta-button">Get Started</button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Mobile Training Section */}
      <MobileTraining data={mobileTraining} />

      {/* Trainers Section */}
      {trainers.length > 0 && (
        <section className="trainers-section" id="trainers">
          <h2 className="section-title">Trainers</h2>
          <div className="trainers-grid">
            {trainers.map((trainer) => (
              <div key={trainer.id} className="trainer-card">
                {trainer.experience && <div className="trainer-experience">{trainer.experience}</div>}
                <div className="trainer-image-container">
                  <SafeImage
                    src={trainer.imageUrl}
                    alt={trainer.name}
                    className="trainer-image"
                    fallback={
                      <div className="trainer-image-placeholder">
                        <span>{trainer.name?.charAt(0) || 'T'}</span>
                      </div>
                    }
                  />
                </div>

                <div className="trainer-info">
                  <h3 className="trainer-name">{trainer.name}</h3>
                  <p className="trainer-specialty">{trainer.specialty}</p>

                  {trainer.bio && (
                    <p className="trainer-bio">{trainer.bio}</p>
                  )}

                  {trainer.certifications && trainer.certifications.length > 0 && (
                    <div className="trainer-certifications">
                      {trainer.certifications.map((cert, index) => (
                        <span key={index} className="cert-badge-small">{cert}</span>
                      ))}
                    </div>
                  )}

                  {trainer.calendlyUrl && (
                    <button
                      type="button"
                      className="trainer-book-btn"
                      onClick={() => openCalendly(trainer.calendlyUrl)}
                    >
                      <Calendar size={18} />
                      <span>Book Session</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Reviews Section */}
      <Reviews reviews={reviews} />

      {/* About Section */}
      <section className="about" id="about">
        <div className="about-content">
          <div className="about-image">
            {about.imageUrl ? (
              <SafeImage
                src={about.imageUrl}
                alt={about.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.3)'}}>
                Photo
              </div>
            )}
          </div>
          <div className="about-text">
            <h2 className="section-title">About</h2>
            <h3 style={{color: 'var(--primary)', marginBottom: '1rem'}}>{about.title}</h3>
            {about.content && about.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
            {!about.content && (
              <>
                <p>With over a decade of experience transforming lives through fitness, I've helped hundreds of clients achieve goals they never thought possible.</p>
                <p>Whether you're looking to lose weight, build muscle, improve athletic performance, or simply feel better in your daily life, I'll create a customized plan that fits your unique needs and challenges.</p>
              </>
            )}
            <div className="certifications">
              {about.certifications && about.certifications.length > 0 ? (
                about.certifications.map((cert, idx) => (
                  <span key={idx} className="cert-badge">{cert}</span>
                ))
              ) : (
                <>
                  <span className="cert-badge">NASM-CPT</span>
                  <span className="cert-badge">Nutrition Specialist</span>
                  <span className="cert-badge">Strength Coach</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      {(instagramUrl || tiktokUrl || facebookUrl) && (
        <section className="landing-section" style={{textAlign: 'center', paddingTop: '2rem', paddingBottom: '2rem'}}>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center', alignItems: 'center'}}>
            {instagramUrl && (
              <a 
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2rem',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '2px solid var(--primary)',
                  color: 'var(--light)',
                  fontFamily: 'Antonio, sans-serif',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--primary)';
                  e.currentTarget.style.color = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)';
                  e.currentTarget.style.color = 'var(--light)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Instagram size={24} />
                Follow Us on Instagram
              </a>
            )}
            {tiktokUrl && (
              <a 
                href={tiktokUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2rem',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '2px solid var(--primary)',
                  color: 'var(--light)',
                  fontFamily: 'Antonio, sans-serif',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--primary)';
                  e.currentTarget.style.color = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)';
                  e.currentTarget.style.color = 'var(--light)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                Follow Us on TikTok
              </a>
            )}
            {facebookUrl && (
              <a 
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2rem',
                  background: 'rgba(255, 0, 0, 0.1)',
                  border: '2px solid var(--primary)',
                  color: 'var(--light)',
                  fontFamily: 'Antonio, sans-serif',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--primary)';
                  e.currentTarget.style.color = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)';
                  e.currentTarget.style.color = 'var(--light)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Facebook size={24} />
                Follow Us on Facebook
              </a>
            )}
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <section className="gallery-section">
          <h2 className="section-title">Gallery</h2>
          <div className="gallery-carousel-wrapper">
            {gallery.length > 4 && (
              <button 
                className="gallery-carousel-arrow gallery-carousel-left"
                onClick={() => {
                  if (galleryScrollRef.current) {
                    galleryScrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll left"
              >
                <ChevronLeft size={32} />
              </button>
            )}
            <div className="gallery-carousel" ref={galleryScrollRef}>
              {gallery.map((image, index) => (
                <div 
                  key={image.id} 
                  className="gallery-carousel-item"
                  onClick={() => {
                    setCurrentGalleryIndex(index);
                    setShowGalleryModal(true);
                  }}
                >
                  <SafeImage src={image.imageUrl} alt={`Gallery ${index + 1}`} className="gallery-carousel-image" />
                </div>
              ))}
            </div>
            {gallery.length > 4 && (
              <button 
                className="gallery-carousel-arrow gallery-carousel-right"
                onClick={() => {
                  if (galleryScrollRef.current) {
                    galleryScrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll right"
              >
                <ChevronRight size={32} />
              </button>
            )}
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="contact" id="contact">
        <div className="contact-content">
          <h2>{contact.title}</h2>
          <p>{contact.subtitle || "Take the first step towards your transformation. Fill out the form below or send us a message."}</p>
          
          <div className="contact-buttons">
            <button onClick={() => setShowContactModal(true)} className="cta-button cta-button--static">
              <MessageCircle size={20} />
              <span>Send Message</span>
            </button>
            <button onClick={() => setShowTrainingModal(true)} className="cta-button-secondary">
              <Calendar size={20} />
              <span>Book Appointment</span>
            </button>
          </div>
        </div>
      </section>

      {/* Floating contact — curved label above bubble (top center) */}
      <div className="floating-contact-wrap">
        <svg className="floating-contact-curve" viewBox="-6 -6 124 36" aria-hidden="true">
          <defs>
            <path
              id="floating-contact-text-arc"
              d="M 10,26 A 48,48 0 0,1 102,26"
              fill="none"
            />
          </defs>
          <text className="floating-contact-curve-text" textAnchor="middle">
            <textPath href="#floating-contact-text-arc" startOffset="50%">
              WhatsApp | Call now
            </textPath>
          </text>
        </svg>
        <button type="button" onClick={() => setShowContactModal(true)} className="floating-contact-btn" aria-label="Contact us — WhatsApp or call">
          <MessageCircle size={24} />
        </button>
      </div>

      {/* Footer */}
      <footer>
        <p className="footer-text">{footer.copyright || '© 2026 Bodyworks Training LLC. All rights reserved.'}</p>
        <div className="footer-admin">
          <Link to="/admin" className="footer-admin-link">Admin Portal</Link>
        </div>
      </footer>
    </>
  );
}

export default Home;
